import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Table,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Loader,
  Center,
  Select,
  Modal,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import {
  IconSearch,
  IconArrowLeft,
  IconRefresh,
  IconTrash,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import {
  useAdminSessionListQuery,
  useRevokeRefreshTokenMutation,
} from "generated/graphql";

/**
 * SessionManagement (Admin)
 *
 * Lists all user sessions with filtering and revocation capabilities.
 * Shows session details including IP, user agent, and activity times.
 */
export default function SessionManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [userIdFilter, setUserIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>("active");
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);

  // Convert status filter to boolean
  const isActiveFilter = useMemo(() => {
    if (statusFilter === "active") return true;
    if (statusFilter === "inactive") return false;
    return undefined;
  }, [statusFilter]);

  // Parse userId filter - only use if it's a valid UUID
  const parsedUserId = useMemo(() => {
    const trimmed = userIdFilter.trim();
    if (!trimmed) return undefined;
    // Basic UUID validation
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(trimmed) ? trimmed : undefined;
  }, [userIdFilter]);

  const { data, loading, error, refetch } = useAdminSessionListQuery({
    variables: {
      userId: parsedUserId,
      isActive: isActiveFilter,
    },
    fetchPolicy: "cache-and-network",
  });

  const [revokeToken, { loading: revoking }] = useRevokeRefreshTokenMutation();

  const sessions = useMemo(() => data?.listSessions ?? [], [data]);

  // Calculate if session is expired based on TTL
  function isExpired(createdAt: string, ttlMinutes: number): boolean {
    const created = new Date(createdAt);
    const expires = new Date(created.getTime() + ttlMinutes * 60 * 1000);
    return expires < new Date();
  }

  // Format date for display
  function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString();
  }

  // Get session status badge
  function getStatusBadge(
    isActive: boolean,
    createdAt: string,
    ttlMinutes: number,
    revokedAt: string | null | undefined
  ) {
    if (revokedAt) {
      return (
        <Badge color="red" variant="light">
          Revoked
        </Badge>
      );
    }
    if (!isActive) {
      return (
        <Badge color="gray" variant="light">
          Inactive
        </Badge>
      );
    }
    if (isExpired(createdAt, ttlMinutes)) {
      return (
        <Badge color="yellow" variant="light">
          Expired
        </Badge>
      );
    }
    return (
      <Badge color="green" variant="light">
        Active
      </Badge>
    );
  }

  // Truncate token for display
  function truncateToken(token: string): string {
    if (token.length <= 16) return token;
    return `${token.slice(0, 8)}...${token.slice(-8)}`;
  }

  // Parse user agent for display
  function parseUserAgent(ua: string | null | undefined): string {
    if (!ua) return "Unknown";
    // Simple parsing - could be enhanced
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return ua.slice(0, 30) + (ua.length > 30 ? "..." : "");
  }

  async function handleRevoke() {
    if (!selectedToken) return;

    try {
      const result = await revokeToken({
        variables: { refreshToken: selectedToken },
      });

      if (result.data?.revokeRefreshToken) {
        notifications.show({
          title: "Session Revoked",
          message: "The session has been successfully revoked",
          color: "green",
        });
        refetch();
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to revoke session",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to revoke session",
        color: "red",
      });
    }
    setRevokeModalOpen(false);
    setSelectedToken(null);
  }

  function openRevokeModal(token: string) {
    setSelectedToken(token);
    setRevokeModalOpen(true);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub")}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>

      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
          Session Management
        </Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          View and manage user authentication sessions. Sessions can be revoked to
          force users to re-authenticate.
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <TextInput
            placeholder="Filter by User ID (UUID)"
            leftSection={<IconSearch size={16} />}
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.currentTarget.value)}
            className="w-full sm:max-w-xs"
            error={
              userIdFilter.trim() && !parsedUserId
                ? "Enter a valid UUID"
                : undefined
            }
          />
          <Select
            data={[
              { value: "all", label: "All Sessions" },
              { value: "active", label: "Active Only" },
              { value: "inactive", label: "Inactive/Revoked" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-full sm:w-40"
          />
          <Button
            variant="light"
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loading}
            size="sm"
          >
            Refresh
          </Button>
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Status</Table.Th>
                <Table.Th>User ID</Table.Th>
                <Table.Th>IP Address</Table.Th>
                <Table.Th>Browser</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Last Used</Table.Th>
                <Table.Th w={80}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && sessions.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c="red">
                      Failed to load sessions.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : sessions.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      No sessions found matching your filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                sessions.map((session) => (
                  <Table.Tr key={session.seqId}>
                    <Table.Td>
                      {getStatusBadge(
                        session.isActive,
                        session.createdAt,
                        session.ttlMinutes,
                        session.revokedAt
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={session.userId}>
                        <Text size="xs" ff="monospace" className="cursor-help">
                          {session.userId.slice(0, 8)}...
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {session.sessionDetails?.ipAddress || "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={session.sessionDetails?.userAgent || "Unknown"}>
                        <Text size="sm" className="cursor-help">
                          {parseUserAgent(session.sessionDetails?.userAgent)}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{formatDate(session.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{formatDate(session.lastAccessedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      {session.isActive && !session.revokedAt && (
                        <Tooltip label="Revoke session">
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => openRevokeModal(session.token)}
                            disabled={revoking}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <Text
          size="sm"
          c={isDark ? "gray.5" : "gray.6"}
          className="text-center sm:text-left"
        >
          Showing {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </Text>
      </div>

      {/* Revoke Confirmation Modal */}
      <Modal
        opened={revokeModalOpen}
        onClose={() => {
          setRevokeModalOpen(false);
          setSelectedToken(null);
        }}
        title="Revoke Session?"
        centered
      >
        <Text size="sm" mb="lg">
          Are you sure you want to revoke this session? The user will need to
          re-authenticate.
        </Text>
        <Group justify="flex-end">
          <Button
            variant="light"
            color="gray"
            onClick={() => {
              setRevokeModalOpen(false);
              setSelectedToken(null);
            }}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleRevoke} loading={revoking}>
            Revoke Session
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
